import { Box, Flex, Text } from '@pancakeswap/uikit'
import Image from 'next/image'
import Link from 'next/link'
import styled from 'styled-components'
import useSWR from 'swr'
import ConnectWalletButton from 'components/ConnectWalletButton'
import MyReferralLink from 'views/Social/MyReferralLink'
import Shell, { ActionButton, Panel, useV3Ready } from './components/Shell'
import YourClaims from './components/YourClaims'
import { useV3Tx } from './components/useV3Tx'
import { competitionV3Abi } from './abi'
import { TIERS, V3_ADDRESS, V3_API_URL, fetcher, formatUnits18 } from './config'
import { Bar, Display, Eyebrow, Num } from './ui'

const Grid = styled(Box)`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
  }
`
const Tier = styled(Flex)<{ $on: boolean }>`
  justify-content: space-between;
  min-height: 44px;
  align-items: center;
  padding: 0 14px;
  border-radius: 12px;
  margin-bottom: 6px;
  background: ${({ theme, $on }) => ($on ? theme.colors.tertiary : 'transparent')};
  border: 1px solid ${({ theme, $on }) => ($on ? theme.colors.primary : theme.colors.cardBorder)};
`
const Stat = ({ k, children, testId }: { k: string; children: React.ReactNode; testId?: string }) => (
  <Box style={{ minWidth: 0 }}>
    <Text fontSize="12px" color="textSubtle">{k}</Text>
    <Display bold fontSize="26px" lineHeight="30px" data-testid={testId} style={{ overflowWrap: 'anywhere' }}>{children}</Display>
  </Box>
)

export default function ReferralV3() {
  const { ready, address } = useV3Ready()
  const { send, pending } = useV3Tx()
  const a = address?.toLowerCase()
  const { data: r, mutate } = useSWR(a ? `${V3_API_URL}/referral/${a}` : null, fetcher, { refreshInterval: 30000 })
    const paid = r?.paidCompetitions ?? 0
  const nextAt = TIERS.map((t) => Number(t.range.split('–')[0].replace('+', ''))).find((n) => n > paid)
  return (
    <Shell title="Rewards & referral" subtitle="Claim what you won, and earn a share of the creation fee on every competition made with your link.">
      {!a && (
        <Panel style={{ textAlign: 'center' }} data-testid="v3-connect">
          <Image src="/images/brand/business.webp" alt="" width={120} height={120} unoptimized style={{ margin: '0 auto', display: 'block', height: 'auto', borderRadius: 24 }} />
          <Display bold fontSize="22px" mt="8px">Connect a wallet to see your rewards</Display>
          <Text color="textSubtle" mt="8px" mb="16px">Your prizes, your referral tier and your commission all live on BNB Smart Chain.</Text>
          <Box style={{ maxWidth: 320, margin: '0 auto' }}><ConnectWalletButton style={{ minHeight: 48, width: '100%' }}>Connect wallet</ConnectWalletButton></Box>
        </Panel>
      )}
      {a && (
        <Grid>
          <Panel data-testid="v3-rewards">
            <Eyebrow>Your rewards</Eyebrow>
            {/* both contracts, one Claim button per prize (moved here from the list hero, 2026-10-06; the list links here with "N to claim") */}
            <YourClaims />
          </Panel>

          <Panel data-testid="v3-referral">
            <Eyebrow>Referral commission</Eyebrow>
            <Text fontSize="14px" color="textSubtle" mt="8px">
              A share of the BNB creation fee on every paid competition created with your link. Free creations and self-referrals do not count. Only you can withdraw it.
            </Text>
            <Flex mt="16px" style={{ gap: 24 }} flexWrap="wrap">
              <Stat k="Paid creations"><Num>{paid}</Num></Stat>
              <Stat k="Next rate"><Num>{((r?.nextRateBps ?? 2000) / 100).toFixed(0)}%</Num></Stat>
              <Stat k="Withdrawable" testId="v3-ref-credit"><Num>{formatUnits18(r?.creditWei, 18, 6)} BNB</Num></Stat>
            </Flex>
            <Box mt="16px">
              {TIERS.map((t, i) => (
                <Tier key={t.range} $on={r?.tier === i + 1}><Text>{t.range} paid creations</Text><Text bold><Num>{t.bps / 100}%</Num></Text></Tier>
              ))}
            </Box>
            {nextAt ? (
              <Box mt="8px">
                <Bar $pct={Math.min(100, (paid / nextAt) * 100)} />
                <Text fontSize="12px" color="textSubtle" mt="4px">{r?.toNextTier ?? nextAt - paid} more paid creations to the next tier</Text>
              </Box>
            ) : null}
            <Box mt="16px">
              <ActionButton disabled={!ready || pending || !r || r.creditWei === '0'} data-testid="v3-ref-withdraw"
                onClick={async () => (await send('Withdraw commission', { address: V3_ADDRESS, abi: competitionV3Abi, functionName: 'withdrawReferralCredit' })) && mutate()}>
                Withdraw commission
              </ActionButton>
            </Box>
            {address && (
              <Box mt="16px">
                <MyReferralLink wallet={address} />
                <Text fontSize="12px" color="textSubtle" mt="6px">Your affiliate tiers, earnings with their Claim buttons and the shiller kit are on your profile: <Link href="/u#earn" data-testid="v3-earn-link" style={{ textDecoration: 'underline' }}>Earn</Link>.</Text>
              </Box>
            )}
            <Text fontSize="12px" color="textSubtle" mt="12px">{r?.note}</Text>
          </Panel>
        </Grid>
      )}
    </Shell>
  )
}
